import { DatePicker, Popover } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { TimeRange } from 'features/systemLens'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import {
  Activity,
  Calendar,
  CalendarDays,
  Clock1,
  Clock8,
  Database,
  FileStack,
  SunMedium,
  TrendingUp,
} from 'lucide-react'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { ChartTimeUnit } from 'types'
import { updateColorOpacity } from 'utils/colorHelpers'

import { TimeRangeDisplay } from './TimeRangeDisplay'

const { RangePicker } = DatePicker

const ClickableRangeSection = styled.div<{
  $isClickable: boolean
  $isCustom?: boolean
}>`
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  display: flex;
  align-items: center;
  gap: 0;
  background-color: ${({ $isCustom, theme }) =>
    $isCustom && theme.forecast.primary
      ? updateColorOpacity(theme.forecast.primary, 0.15)
      : 'transparent'};
  border-radius: 0.25rem;
  padding: 0.125rem 0.25rem;

  @container (max-width: 200px) {
    display: flex;
    flex-wrap: wrap;
    width: 100%;

    span {
      display: inline;
    }
  }

  &:hover {
    background: ${({ $isCustom, theme }) =>
      $isCustom && theme.forecast.primary
        ? updateColorOpacity(theme.forecast.primary, 0.25)
        : theme.forecast.hoverButton};
  }

  &:active {
    background: ${({ $isCustom, theme }) =>
      $isCustom && theme.forecast.primary
        ? updateColorOpacity(theme.forecast.primary, 0.3)
        : theme.forecast.activeButton};
  }
`

const PopoverContentWrapper = styled.div`
  width: 28rem;
  padding: 1rem;
`

const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
`

const PresetButton = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  background: ${({ $isActive, theme }) =>
    $isActive ? 'rgba(0, 194, 144, 0.1)' : theme.forecast.background3};
  color: ${({ $isActive, theme }) =>
    $isActive ? '#00c290' : theme.forecast.text1};
  border: 1px solid
    ${({ $isActive, theme }) =>
      $isActive ? 'rgba(0, 194, 144, 0.3)' : theme.forecast.border};
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: ${({ $isActive }) => ($isActive ? '600' : '500')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover:not(:disabled) {
    background: ${({ $isActive, theme }) =>
      $isActive ? 'rgba(0, 194, 144, 0.15)' : theme.forecast.pageBackground1};
    border-color: ${({ $isActive, theme }) =>
      $isActive ? 'rgba(0, 194, 144, 0.5)' : theme.forecast.text2};
  }

  svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }
`

const SectionLabel = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.forecast.text2};
  margin-bottom: 0.5rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03125rem;
`

const CustomRangeSection = styled.div`
  padding-top: 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.forecast.border};
`

const StyledRangePicker = styled(RangePicker)`
  width: 100%;
  background: ${({ theme }) => theme.forecast.background3} !important;
  border-color: ${({ theme }) => theme.forecast.border} !important;

  &:hover {
    border-color: ${({ theme }) => theme.forecast.text2} !important;
  }

  &.ant-picker-focused {
    border-color: #00c290 !important;
    box-shadow: 0 0 0 2px rgba(0, 194, 144, 0.1) !important;
  }

  .ant-picker-input > input {
    color: ${({ theme }) => theme.forecast.text1} !important;
    background: transparent !important;
  }

  .ant-picker-suffix {
    color: ${({ theme }) => theme.forecast.text2} !important;
  }

  .ant-picker-separator {
    color: ${({ theme }) => theme.forecast.text2} !important;
  }
`

interface PredefinedRange {
  label: string
  value: TimeRange['preset']
  icon: React.ReactNode
  getRange: () => [dayjs.Dayjs, dayjs.Dayjs]
  relativeLookback?: {
    value: number
    unit: ChartTimeUnit
  }
}

const predefinedRanges: PredefinedRange[] = [
  {
    label: 'Last Hour',
    value: 'last-hour',
    icon: <Clock1 />,
    getRange: () => [dayjs().subtract(1, 'hour'), dayjs()],
    relativeLookback: { value: 1, unit: ChartTimeUnit.HOURS },
  },
  {
    label: 'Last 8 Hours',
    value: 'last-8-hours',
    icon: <Clock8 />,
    getRange: () => [dayjs().subtract(8, 'hours'), dayjs()],
    relativeLookback: { value: 8, unit: ChartTimeUnit.HOURS },
  },
  {
    label: 'Last 24 Hours',
    value: 'last-24-hours',
    icon: <SunMedium />,
    getRange: () => [dayjs().subtract(24, 'hours'), dayjs()],
    relativeLookback: { value: 24, unit: ChartTimeUnit.HOURS },
  },
  {
    label: 'Last 7 Days',
    value: 'last-7-days',
    icon: <Activity />,
    getRange: () => [dayjs().subtract(7, 'days'), dayjs()],
    relativeLookback: { value: 7, unit: ChartTimeUnit.DAYS },
  },
  {
    label: 'Last 30 Days',
    value: 'last-30-days',
    icon: <TrendingUp />,
    getRange: () => [dayjs().subtract(30, 'days'), dayjs()],
    relativeLookback: { value: 30, unit: ChartTimeUnit.DAYS },
  },
  {
    label: 'Last 3 Months',
    value: 'last-3-months',
    icon: <FileStack />,
    getRange: () => [dayjs().subtract(3, 'months'), dayjs()],
    relativeLookback: { value: 90, unit: ChartTimeUnit.DAYS },
  },
  {
    label: 'Last Year',
    value: 'last-year',
    icon: <Calendar />,
    getRange: () => [dayjs().subtract(1, 'year'), dayjs()],
    relativeLookback: { value: 365, unit: ChartTimeUnit.DAYS },
  },
  {
    label: 'Last 5 Years',
    value: 'last-5-years',
    icon: <Database />,
    getRange: () => [dayjs().subtract(2, 'years'), dayjs()],
    relativeLookback: { value: 365 * 5, unit: ChartTimeUnit.DAYS },
  },
  {
    label: 'YTD',
    value: 'ytd',
    icon: <CalendarDays />,
    getRange: () => [dayjs().startOf('year'), dayjs()],
  },
]

interface ActiveTimeRangeSectionWithPopoverProps {
  isCustom: boolean
  onClick: () => void
  fromTime: dayjs.Dayjs
  toTime: dayjs.Dayjs
  timeRange: TimeRange
  onRangeClick?: () => void
  showDateConfig?: boolean
  onOpenChange?: (open: boolean) => void
  onClose?: () => void
}

export const ActiveTimeRangeSectionWithPopover: React.FC<
  ActiveTimeRangeSectionWithPopoverProps
> = ({
  isCustom,
  onClick,
  fromTime,
  toTime,
  timeRange,
  onRangeClick,
  showDateConfig = false,
  onOpenChange,
  onClose,
}) => {
  const {
    setTimeRange,
    liveTail,
    setLiveTail,
    dateMode,
    setDateMode,
    relativeStart,
    setRelativeStart,
    relativeEnd,
    setRelativeEnd,
  } = useScatterplotContext()
  const [selectedPreset, setSelectedPreset] = useState<PredefinedRange | null>(
    null
  )
  const [intervalValue, setIntervalValue] = useState<number>(
    timeRange.interval || 15
  )
  const [intervalUnit, setIntervalUnit] = useState<ChartTimeUnit>(
    timeRange.intervalUnit || ChartTimeUnit.MINUTES
  )

  useEffect(() => {
    if (dateMode === 'relative') {
      const presetByValue = predefinedRanges.find(
        (range) =>
          range.relativeLookback?.value === relativeStart.value &&
          range.relativeLookback?.unit === relativeStart.unit
      )
      setSelectedPreset(presetByValue ?? null)
    } else if (dateMode === 'absolute') {
      if (timeRange.from === dayjs().startOf('year').toISOString()) {
        const ytdPreset = predefinedRanges.find(
          (range) => range.value === 'ytd'
        )
        setSelectedPreset(ytdPreset ?? null)
      } else setSelectedPreset(null)
    }
  }, [fromTime, toTime, dateMode, relativeStart, relativeEnd, timeRange])

  useEffect(() => {
    if (timeRange.interval && timeRange.intervalUnit) {
      setIntervalValue(timeRange.interval)
      setIntervalUnit(timeRange.intervalUnit)
    }
  }, [timeRange.interval, timeRange.intervalUnit])

  const handlePresetSelect = (range: PredefinedRange) => {
    setSelectedPreset(range)
    const [from, to] = range.getRange()

    if (range.relativeLookback) {
      setDateMode('relative')
      setRelativeStart(range.relativeLookback)
      setRelativeEnd({ isNow: true, value: 0, unit: ChartTimeUnit.DAYS })
      setLiveTail(true)
    } else {
      setDateMode('absolute')
      setLiveTail(true)
    }

    setTimeRange({
      from: from.toISOString(),
      to: to.toISOString(),
      preset: range.value,
      interval: timeRange.interval,
      intervalUnit: timeRange.intervalUnit,
    })

    if (onClose) {
      onClose()
    }
  }

  const rangeSectionContent = (
    <ClickableRangeSection
      $isClickable={true}
      $isCustom={isCustom}
      onClick={onClick}>
      <TimeRangeDisplay
        fromTime={fromTime}
        toTime={toTime}
        liveTail={liveTail}
        timeRange={timeRange}
      />
    </ClickableRangeSection>
  )

  if (onRangeClick) {
    return rangeSectionContent
  }

  const handleCustomRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    if (dates && dates[0] && dates[1]) {
      setSelectedPreset(null)
      setDateMode('absolute')
      setLiveTail(false)
      setTimeRange({
        from: dates[0].toISOString(),
        to: dates[1].toISOString(),
        preset: 'custom',
        interval: intervalValue,
        intervalUnit: intervalUnit,
      })
    }
  }
  return (
    <Popover
      content={
        <PopoverContentWrapper>
          <SectionLabel>Quick Select</SectionLabel>
          <PresetGrid>
            {predefinedRanges.map((range) => (
              <PresetButton
                key={range.value}
                $isActive={selectedPreset?.value === range.value}
                onClick={() => handlePresetSelect(range)}>
                {range.icon}
                {range.label}
              </PresetButton>
            ))}
          </PresetGrid>

          <CustomRangeSection>
            <SectionLabel>Custom Range</SectionLabel>
            <StyledRangePicker
              showTime={{
                format: 'HH:mm',
              }}
              format='YYYY-MM-DD HH:mm'
              value={
                dateMode === 'relative' || selectedPreset
                  ? null
                  : [fromTime, toTime]
              }
              onChange={handleCustomRangeChange}
              placeholder={['Start Time', 'End Time']}
              allowClear
              size='small'
              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
            />
          </CustomRangeSection>
        </PopoverContentWrapper>
      }
      trigger='click'
      open={showDateConfig}
      onOpenChange={onOpenChange}
      placement='top'>
      {rangeSectionContent}
    </Popover>
  )
}
